import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import { Check, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { FEELING_BY_KEY } from "@/lib/feelings";
import { SYSTEM_BY_KEY } from "@/lib/sensory";
import { useLowStim } from "@/contexts/LowStimContext";
import { TID } from "@/lib/testIds";
import BubbleField from "@/components/BubbleField";
import { speak, stopSpeaking } from "@/lib/tts";

// Bubbles matched to a specific feeling. mode = "student" | "explore".
export default function FeelingScreen({ mode = "student" }) {
  const { feeling } = useParams();
  const f = FEELING_BY_KEY[feeling];
  const nav = useNavigate();
  const { enabled: lowStim } = useLowStim();
  const [bubbles, setBubbles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [guidance, setGuidance] = React.useState(null);

  const backTo = mode === "student" ? "/play/feelings" : "/explore/feelings";
  const listEndpoint = mode === "student" ? `/me/feelings/${feeling}` : `/public/feelings/${feeling}`;
  const selectEndpoint = mode === "student" ? "/me/select" : "/public/select";

  React.useEffect(() => {
    if (!f) return;
    setLoading(true);
    api.get(listEndpoint)
      .then(({ data }) => setBubbles(data.bubbles || []))
      .finally(() => setLoading(false));
  }, [feeling, f, listEndpoint]);

  if (!f) return <div>Unknown feeling. <Link to={backTo} className="underline">Back</Link></div>;

  const onPop = async (b) => {
    try {
      const { data } = await api.post(selectEndpoint, { intervention_id: b.id, feeling, entry: "feeling" });
      setGuidance(data.intervention);
      speak(`${data.intervention.bubble_name}. ${data.intervention.guidance_text}`);
    } catch (e) {
      setGuidance(b);
    }
  };

  const closeGuidance = () => { stopSpeaking(); setGuidance(null); };

  const Icon = f.icon;

  return (
    <div data-testid={TID.feelingScreen} className="relative">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <button
          onClick={() => nav(backTo)}
          className="sw-pill h-12"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="display text-xl md:text-2xl font-bold flex items-center gap-2" style={{ color: f.color }}>
          <Icon size={22} /> Feeling {f.prompt.toLowerCase()}
        </div>
      </div>

      <section
        className="rounded-3xl border-2 p-4 md:p-8 relative overflow-hidden"
        style={{ background: f.bg, borderColor: f.border, minHeight: 460 }}
      >
        {loading ? (
          <div className="text-center py-12" style={{ color: f.color }}>Loading strategies…</div>
        ) : bubbles.length === 0 ? (
          <div className="text-center py-12" style={{ color: f.color }}>
            No strategies enabled for this feeling yet.
            {mode === "student" && <div className="text-sm mt-2 opacity-80">Ask your OT to enable a few bubbles.</div>}
          </div>
        ) : (
          <BubbleField
            bubbles={bubbles.map((b) => {
              // color the bubble by its sensory system so users learn the association
              const sys = SYSTEM_BY_KEY[b.system] || {};
              return { ...b, _color: sys.color || f.color, _accent: sys.border || f.border };
            })}
            color={f.color}
            accent={f.accent}
            lowStim={lowStim}
            onPop={onPop}
            testIdFor={(id) => TID.studentBubble(id)}
            perBubbleColor
          />
        )}
      </section>

      <AnimatePresence>
        {guidance && (
          <motion.div
            data-testid={TID.studentGuidance}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={closeGuidance}
          >
            <motion.div
              initial={{ scale: 0.9, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              className="sw-card max-w-lg w-full p-8 text-center" onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff" }}
            >
              <div
                className="mx-auto h-20 w-20 rounded-full flex items-center justify-center text-white"
                style={{ background: `radial-gradient(circle at 32% 28%, #ffffff 0%, ${f.border} 38%, ${f.color} 100%)` }}
              >
                {(() => {
                  const I = (guidance.icon && Lucide[guidance.icon]) || Lucide.Sparkles;
                  return <I size={36} className="text-white" />;
                })()}
              </div>
              <h2 className="display text-3xl font-bold mt-4" style={{ color: f.color }}>{guidance.bubble_name}</h2>
              {guidance.system && (
                <div className="text-xs uppercase font-semibold tracking-wider mt-1" style={{ color: f.color, opacity: 0.75 }}>
                  {guidance.system.replace("_", " ")} strategy
                </div>
              )}
              <p className="mt-3 text-lg text-[var(--sw-ink-soft)] leading-relaxed">{guidance.guidance_text}</p>
              <button
                data-testid={TID.studentGuidanceDone}
                onClick={closeGuidance}
                className="mt-6 sw-pill h-14 px-8 text-lg bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
              >
                <Check size={18} /> Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
